<template>
  <Modal
    ref="createDatasetModal"
    :title="'Create New Dataset'"
    :width="'600px'"
    @closed="clearData"
  >
    <template #modal-main>
      <div class="settings container scrollbar">
        <HelpTip>
          New datasets are empty. Use the Import function to populate datasets.
          Before training, images should be split accordingly into train and validate(val) groups using the Import function.
        </HelpTip>

        <div class="row mt-2">
          <div class="col-12 settings__control-group">
            <div class="input-label d-flex">
              <label class="label required">Dataset Name</label>
              <SVGIcon
                v-tooltip="'It is recommended to give datasets a name can summarize what the data is or its purpose.'"
                :width="'20px'"
                :height="'20px'"
                :iconName="'info'"
                class="tooltip-icon"
              />
            </div>
            <input
              v-model="createNewName"
              class="select"
              type="text"
            >
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-12 settings__control-group">
            <div class="input-label d-flex">
              <label class="label required">Annotation Set Name</label>
              <SVGIcon
                v-tooltip="`A dataset can have many annotation sets.
          Annotation sets are collections of annotations and sensor data (eg. 2D bounding boxes, 3D bounding boxes, Radar, LiDAR, etc.)
          that are used for training.`"
                :width="'20px'"
                :height="'20px'"
                :iconName="'info'"
                class="tooltip-icon"
              />
            </div>
            <input
              v-model="annSetName"
              class="select"
              placeholder="annotations"
            >
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-12 settings__control-group">
            <div class="input-label d-flex">
              <label class="label">Labels</label>
              <SVGIcon
                v-tooltip="`Labels are image classes used by 2D bounding boxes, 3D bounding boxes, and segmentations.
                  Some common labels are suggested (person, car, bicycle). You can add new labels by
                  entering comma separated values. If this input field is blank, no labels will be created. You can always add more labels by clicking
                  the add icon in the 'Labels' section of the dataset card.
                `"
                :width="'20px'"
                :height="'20px'"
                :iconName="'info'"
                class="tooltip-icon"
              />
            </div>
            <textarea
              v-model="labels"
              placeholder="person,car,bicycle"
              rows="2"
              cols="50"
            />
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-12 settings__control-group">
            <div class="input-label d-flex">
              <label class="label">Dataset Description</label>
              <SVGIcon
                v-tooltip="`Describe additional information useful for data curation, training, or validation.`"
                :width="'20px'"
                :height="'20px'"
                :iconName="'info'"
                class="tooltip-icon"
              />
            </div>
            <textarea
              v-model="createNewDescription"
              rows="2"
              cols="50"
            />
          </div>
        </div>

        <div v-if="message" class="row mt-3">
          <div class="col-12 settings__control-group">
            <div class="result error">
              <span>{{ message }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #modal-footer>
      <div class="footer">
        <button class="button" :disabled="!createNewName" @click="createDataset">Create</button>
      </div>
    </template>
  </Modal>
</template>

<script>
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import Modal from '@/components/Modal.vue';
import SVGIcon from '@/components/SVGIcon.vue';
import HelpTip from '../../HelpTip.vue';

export default {
  name: 'DatasetCreateModal',
  components: {
    Modal,
    HelpTip,
    SVGIcon,
  },
  emits: {
    'create-dataset': null,
  },
  data() {
    return {
      dsConn: new DatastoreConnect(this.$store.state.enterpriseServerUrl),
      createNewName: '',
      createNewDescription: '',
      annSetName: 'annotations',
      labels: '',
      message: null,
    };
  },
  computed: {
    user() {
      return this.$store.state.user.user;
    },
  },
  methods: {
    showModal() {
      this.$refs.createDatasetModal.showModal();
    },
    clearData() {
      this.createNewName = '';
      this.createNewDescription = '';
      this.labels = '';
      this.annSetName = 'annotations';
      this.message = null;
    },
    closeModal() {
      this.clearData();
      this.$refs.createDatasetModal.closeModal();
    },
    createDataset() {
      if (!this.createNewName || this.createNewName === "") {
        this.message = 'Missing dataset name';
        return;
      }
      let l = [];
      if (this.labels && this.labels !== "") {
        l = this.labels.split(",");
      }
      l = l.map((e) => e.trim()).filter((e) => e !== "");
      const args = {
        name: this.createNewName,
        description: this.createNewDescription,
        annotation_set_name: this.annSetName,
        labels: l,
      };
      this.$emit('create-dataset', args);
      this.closeModal();
    },
  },
};
</script>

<style lang="scss" scoped>

.settings {
  padding: 0.75rem 3rem;
  max-height: 600px;
  overflow-y: auto;
}

@media (min-width: $breakpoint-sm) {
  .settings {
    min-width: 420px;
  }
}

.close-button {
  @include themify() {
    background: themed('button-secondary');
    color: themed('button-secondary-text');
  }
}

.footer {
  display: flex;
  flex-direction: row;
  padding: 0.75rem 3rem;
  justify-content: flex-end;
}

.result {
  display: flex;
  width: 100%;
  height: 100%;
  border-radius: 5px;
  font-size: 1rem;
  font-weight:600;
  padding: 10px;
  align-items: center;
  justify-content: center;
}

.error {
  @include themify() {
    background: rgba(182, 94, 94, 0.5) ;
    color: themed('color-error')
  }
}

</style>
